<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  processContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import wootConstants from 'dashboard/constants/globals';

import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const searchContacts = createContactSearcher();
const store = useStore();
const { t } = useI18n();
const { width: windowWidth } = useWindowSize();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const viewInModal = computed(() => props.isModal || isSmallScreen.value);

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const selectedDepartment = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const clearFormState = () => {
  Object.assign(formState, {
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
};

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const ROUTING_INBOX_PREFIX = 'WA-Rota:';

const accessibleRouteInboxes = computed(() => {
  const contactInboxes = selectedContact.value?.contactInboxes || [];

  return contactInboxes
    .filter(inbox => inbox?.name?.startsWith(ROUTING_INBOX_PREFIX))
    .map(inbox => ({
      ...inbox,
      department: inbox.name.replace(ROUTING_INBOX_PREFIX, '').trim(),
    }));
});

const allRouteInboxes = computed(() =>
  (inboxesList.value || [])
    .filter(inbox => inbox?.name?.startsWith(ROUTING_INBOX_PREFIX))
    .map(inbox => ({
      ...inbox,
      department: inbox.name.replace(ROUTING_INBOX_PREFIX, '').trim(),
    }))
);

const routeInboxes = computed(() => {
  if (selectedContact.value) {
    return accessibleRouteInboxes.value;
  }

  return allRouteInboxes.value;
});

const departmentOptions = computed(() =>
  routeInboxes.value.map(inbox => ({
    label: inbox.department,
    value: inbox.department,
    inboxId: inbox.id,
    inbox,
  }))
);

const autoSelectSingleDepartment = () => {
  if (departmentOptions.value.length === 1) {
    selectedDepartment.value = departmentOptions.value[0].value;
    targetInbox.value = departmentOptions.value[0].inbox;
  }
};

const selectedDepartmentInbox = computed(() => {
  if (!selectedDepartment.value) return null;

  return routeInboxes.value.find(
    inbox => inbox.department === selectedDepartment.value
  );
});

const handleDepartmentChange = department => {
  selectedDepartment.value = department || null;

  const inbox = selectedDepartmentInbox.value;
  if (inbox) {
    targetInbox.value = inbox;
  }
};

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const composePopoverClass = computed(() => {
  if (viewInModal.value) return '';

  return props.alignPosition === 'right'
    ? 'absolute ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset]'
    : 'absolute rtl:left-0 rtl:right-[unset] ltr:right-0 ltr:left-[unset]';
});

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query);
      // null means the request was aborted (a newer search is in-flight),
      if (results === null) return;
      contacts.value = results;
      isSearching.value = false;
    } catch (error) {
      isSearching.value = false;
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    }
  },
  400,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  contacts.value = [];
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      // Merge the processed contactableInboxes with the inboxesList
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );

      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;

  const matchingRouteInbox = routeInboxes.value.find(
    routeInbox => Number(routeInbox.id) === Number(inbox?.id)
  );

  if (matchingRouteInbox) {
    selectedDepartment.value = matchingRouteInbox.department;
  }

  if (!inbox) {
    selectedDepartment.value = null;
    clearFormState();
  }

  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
  selectedDepartment.value = null;
  clearFormState();
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  selectedDepartment.value = null;
  resetContacts();
  emit('close');
};

const discardCompose = () => {
  clearFormState();
  formState.message = '';
  closeCompose();
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  const department = selectedDepartment.value;
  const routeInbox = selectedDepartmentInbox.value;

  const payloadWithDepartment = {
    ...payload,
    ...(routeInbox ? { inboxId: routeInbox.id } : {}),
    ...(department ? { customAttributes: { department } } : {}),
  };

  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payloadWithDepartment,
      isFromWhatsApp,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    discardCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const toggle = () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
};

watch(
  activeContact,
  (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) {
        clearSelectedContact();
        clearFormState();
        formState.message = '';
      }

      // First process the contactable inboxes to get the right structure
      const processedInboxes = processContactableInboxes(
        currentContact.contactInboxes || []
      );
      // Then Merge processedInboxes with the inboxes list
      selectedContact.value = {
        ...currentContact,
        contactInboxes: mergeInboxDetails(processedInboxes, inboxesList.value),
      };
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  emit('close');
};

const onModalBackdropClick = () => {
  if (!viewInModal.value) return;
  handleClickOutside();
};

onMounted(() => {
  resetContacts();
  autoSelectSingleDepartment();
});

watch(departmentOptions, () => {
  if (!selectedDepartment.value) {
    autoSelectSingleDepartment();
  }
});

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
        emit('close');
        emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div v-on-click-outside="[
    handleClickOutside,
    // Fixed and edge case https://github.com/chatwoot/chatwoot/issues/10785
    // This will prevent closing the compose conversation modal when the editor Create link popup is open
    { ignore: ['dialog.ProseMirror-prompt-backdrop'] },
  ]" class="relative" :class="{
    'z-50': showComposeNewConversation && !viewInModal,
  }">
    <slot name="trigger" :is-open="showComposeNewConversation" :toggle="toggle" />
    <div v-if="showComposeNewConversation" :class="{
      'fixed z-50 bg-n-alpha-black1 backdrop-blur-[4px] flex items-start pt-[clamp(3rem,15vh,12rem)] justify-center inset-0':
        viewInModal,
    }" @click.self="onModalBackdropClick">
      <ComposeNewConversationForm :form-state="formState" :class="[{ 'mt-2': !viewInModal }, composePopoverClass]"
        :contacts="contacts" :contact-id="contactId" :is-loading="isSearching" :current-user="currentUser"
        :selected-contact="selectedContact" :target-inbox="targetInbox" :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes" :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags" :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature" :send-with-signature="sendWithSignature"
        :department-options="departmentOptions" :selected-department="selectedDepartment"
        :has-route-departments="departmentOptions.length > 0"
        @update-department="handleDepartmentChange" @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts" @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox" @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation" @discard="discardCompose" />
    </div>
  </div>
</template>